import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import SecondaryButton from '../../../shared/components/SecondaryButton'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import BrandHeader from '../components/BrandHeader'
import AuthLayout from '../components/AuthLayout'
import PasswordStrengthBar from '../components/PasswordStrengthBar'
import GoogleIcon from '../../../shared/components/GoogleIcon'
import type { CompanyFormData, LoginFormData } from '../../../shared/utils/types'
import { useAuth, MultiploTenantError, type User, type EmpresaOpcao } from '../../../shared/contexts/AuthContext'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import { clearRememberedDestination, getLoginDestination, rememberLoginDestination } from '../../../shared/utils/loginDestination'
import Dialog from '@mui/material/Dialog'
import { DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemAvatar, ListItemText, Avatar } from '@mui/material'
import { WavingHandOutlined, ApartmentOutlined, CheckCircleOutlined } from '@mui/icons-material'

// Formulário de mudança de palavra-passe no primeiro login. `passwordActual`
// só é preenchido pelo utilizador quando não a conhecemos do próprio login
// (ex: login Google) — ver `firstLoginPassword`.
interface FirstLoginPasswordForm {
    passwordActual: string
    novaPassword: string
    confirmarPassword: string
}

// Backend expõe o fluxo OAuth2 do Spring Security fora do prefixo /api das
// chamadas via axios — é uma navegação de browser completa (redirect para o
// Google), não um pedido AJAX, por isso usamos <a href> em vez de `api.*`.
const GOOGLE_OAUTH_URL = `${import.meta.env.VITE_API_URL || ''}/api/oauth2/authorization/google`

// Mapeia o parâmetro ?erro= que o OAuth2SuccessHandler/failureHandler do
// backend acrescenta ao redirecionar de volta para cá (é sempre uma
// navegação de browser, nunca JSON, por isso o erro só pode chegar assim).
// O caso "sem conta" já não é um erro — o backend redirige com
// ?googleSemConta=1 e a UI oferece criar uma empresa (ver abaixo).
const GOOGLE_ERROR_KEYS: Record<string, string> = {
    'oauth-inactivo': 'login.googleInactive',
    'oauth': 'login.googleError',
}

export default function Login() {
    const { t } = useTranslation(['auth', 'common'])
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    // A página que o utilizador abriu antes de lhe pedirem o login (ex.: o botão de um e-mail) —
    // é para lá que volta depois de entrar, não para a raiz. Lida uma vez: os setSearchParams
    // abaixo substituem a entrada do histórico e levam o state com eles.
    const [destination] = useState(() => getLoginDestination(location.state))
    const [showPw, setShowPw] = useState(false)
    const [apiError, setApiError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [welcomeOpen, setWelcomeOpen] = useState(false)
    const [welcomePhase, setWelcomePhase] = useState<'intro' | 'form' | 'done'>('intro')
    const [welcomeName, setWelcomeName] = useState('')
    const [pendingUser, setPendingUser] = useState<User | null>(null)
    // Palavra-passe com que o utilizador acabou de entrar — reutilizada como
    // `passwordActual` na mudança de palavra-passe do primeiro login, para não
    // a voltar a pedir. Fica vazia num login Google (não há palavra-passe).
    const [firstLoginPassword, setFirstLoginPassword] = useState('')
    const [pwdApiError, setPwdApiError] = useState('')
    const [isChangingPassword, setIsChangingPassword] = useState(false)
    const [tenantOptions, setTenantOptions] = useState<EmpresaOpcao[] | null>(null)
    const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string; rememberMe?: boolean } | null>(null)
    // true quando o selector de empresa está aberto por causa de "Entrar com
    // Google" (email associado a várias empresas) em vez do login normal —
    // decide se handleTenantChosen termina com completeGoogleLogin ou login.
    const [isGoogleTenantChoice, setIsGoogleTenantChoice] = useState(false)
    const [isChoosingTenant, setIsChoosingTenant] = useState(false)
    // Login com Google de um email sem conta associada — o backend redirige
    // com ?googleSemConta=1 e guarda a identidade Google num cookie; aqui
    // perguntamos se quer criar uma empresa nova e, se sim, recolhemos
    // nome/telefone da empresa para a criar.
    const [noAccountEmail, setNoAccountEmail] = useState<string | null>(null)
    const [noAccountPhase, setNoAccountPhase] = useState<'confirm' | 'form'>('confirm')
    const [isCreatingCompany, setIsCreatingCompany] = useState(false)
    const { login, completeGoogleLogin, completeLogin, isAuthenticated } = useAuth()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        defaultValues: { email: '', password: '', rememberMe: false },
    })

    const {
        register: registerCompany,
        handleSubmit: handleSubmitCompany,
        reset: resetCompany,
        formState: { errors: companyErrors },
    } = useForm<CompanyFormData>({
        defaultValues: { nome: '', telefone: '' },
    })

    const {
        register: registerPwd,
        handleSubmit: handleSubmitPwd,
        watch: watchPwd,
        reset: resetPwd,
        setError: setPwdFieldError,
        formState: { errors: pwdErrors },
    } = useForm<FirstLoginPasswordForm>({
        defaultValues: { passwordActual: '', novaPassword: '', confirmarPassword: '' },
    })
    const novaPasswordValue = watchPwd('novaPassword')

    useEffect(() => {
        const erro = searchParams.get('erro')
        if (!erro) return
        setApiError(t(GOOGLE_ERROR_KEYS[erro] ?? GOOGLE_ERROR_KEYS.oauth))
        setSearchParams((params) => {
            params.delete('erro')
            return params
        }, { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só deve correr quando o parâmetro na URL muda
    }, [searchParams])

    useEffect(() => {
        if (searchParams.get('googleEmpresas') !== '1') return
        setSearchParams((params) => {
            params.delete('googleEmpresas')
            return params
        }, { replace: true })

        api.get('/api/auth/google/tenants')
            .then((response) => {
                setTenantOptions(response.data.empresas)
                setIsGoogleTenantChoice(true)
            })
            .catch(() => {
                setApiError(t('login.googleSessionExpired'))
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só deve correr quando o parâmetro na URL muda
    }, [searchParams])

    useEffect(() => {
        if (searchParams.get('googleSemConta') !== '1') return
        setSearchParams((params) => {
            params.delete('googleSemConta')
            return params
        }, { replace: true })

        api.get('/api/public/tenants/register/google/pendente')
            .then((response) => {
                setNoAccountEmail(response.data.email)
                setNoAccountPhase('confirm')
            })
            .catch(() => {
                setApiError(t('login.googleSessionExpired'))
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só deve correr quando o parâmetro na URL muda
    }, [searchParams])

    // `replace`: o /login sai do histórico — "Retroceder" na página aberta não volta ao formulário.
    const goToDestination = () => {
        clearRememberedDestination()
        navigate(destination ?? '/', { replace: true })
    }

    const handleLoggedUser = (loggedUser: User, usedPassword = '') => {
        if (loggedUser.firstLogged === false) {
            setPendingUser(loggedUser)
            setWelcomeName(loggedUser.nome || t('login.welcomeDefaultName'))
            setFirstLoginPassword(usedPassword)
            setWelcomePhase('intro')
            setPwdApiError('')
            resetPwd()
            setWelcomeOpen(true)
        } else {
            completeLogin(loggedUser)
            goToDestination()
        }
    }

    const onSubmit = async (data: LoginFormData) => {
        setApiError('')
        setIsSubmitting(true)
        try {
            const loggedUser = await login({
                email: data.email,
                password: data.password,
                rememberMe: data.rememberMe
            })
            handleLoggedUser(loggedUser, data.password)
        } catch (err) {
            if (err instanceof MultiploTenantError) {
                setPendingCredentials({ email: data.email, password: data.password, rememberMe: data.rememberMe })
                setTenantOptions(err.empresas)
            } else {
                setApiError(t('login.invalidCredentials'))
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleTenantChosen = async (tenantId: string) => {
        if (!isGoogleTenantChoice && !pendingCredentials) return

        setApiError('')
        setIsChoosingTenant(true)
        try {
            const usedPassword = isGoogleTenantChoice ? '' : (pendingCredentials?.password ?? '')
            const loggedUser = isGoogleTenantChoice
                ? await completeGoogleLogin(tenantId)
                : await login({ ...pendingCredentials!, tenantId })
            setTenantOptions(null)
            setPendingCredentials(null)
            setIsGoogleTenantChoice(false)
            handleLoggedUser(loggedUser, usedPassword)
        } catch {
            setApiError(isGoogleTenantChoice ? t('login.googleError') : t('login.invalidCredentials'))
            setTenantOptions(null)
            setPendingCredentials(null)
            setIsGoogleTenantChoice(false)
        } finally {
            setIsChoosingTenant(false)
        }
    }

    const closeNoAccount = () => {
        setNoAccountEmail(null)
        setNoAccountPhase('confirm')
        resetCompany()
    }

    const onCreateCompany = async (data: CompanyFormData) => {
        setApiError('')
        setIsCreatingCompany(true)
        try {
            await api.post('/api/public/tenants/register/google/completar', data)
            closeNoAccount()
            // Mesmo ecrã de sucesso do registo por Google — a empresa está a
            // ser provisionada e o utilizador recebe um email quando estiver pronta.
            navigate('/register?googleSignup=pendente')
        } catch {
            setApiError(t('login.googleSignupFailed'))
            closeNoAccount()
        } finally {
            setIsCreatingCompany(false)
        }
    }

    const finishFirstLogin = () => {
        if (!pendingUser) return
        completeLogin(pendingUser)
        setWelcomeOpen(false)
        goToDestination()
    }

    // Muda a palavra-passe já no primeiro login, sem sair do diálogo. A sessão
    // (cookie httpOnly) foi emitida pela resposta do login, por isso o PUT
    // autenticado funciona mesmo antes de `completeLogin`.
    const onChangeFirstPassword = async (data: FirstLoginPasswordForm) => {
        setPwdApiError('')
        setIsChangingPassword(true)
        try {
            await api.put('/api/auth/password', {
                passwordActual: firstLoginPassword || data.passwordActual,
                novaPassword: data.novaPassword,
            })
            setWelcomePhase('done')
        } catch (err) {
            // Palavra-passe actual errada — o backend responde 400 BAD_REQUEST.
            if (getBackendError(err)?.code === 'BAD_REQUEST') {
                // Revela o campo "palavra-passe actual" (escondido quando a
                // conhecíamos do login) para o utilizador a poder corrigir.
                setFirstLoginPassword('')
                setPwdFieldError('passwordActual', {
                    type: 'server',
                    message: t('login.currentPasswordWrong'),
                })
            } else {
                setPwdApiError(t('login.changePasswordError'))
            }
        } finally {
            setIsChangingPassword(false)
        }
    }

    if (!isAuthenticated || welcomeOpen) {
        return (
            <>
                <AuthLayout>
                    <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider' }}>
                        <BrandHeader />

                        <Typography variant="h4" sx={{ fontSize: '1.05rem', mb: 1.25 }}>
                            {t('login.title')}
                        </Typography>


                        {apiError && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                {apiError}
                            </Alert>
                        )}

                        <Box
                            component="form"
                            onSubmit={handleSubmit(onSubmit)}
                            noValidate
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                        >
                            <TextField
                                label={t('common:fields.email')}
                                type="email"
                                autoComplete="email"
                                autoFocus
                                placeholder={t('login.emailPlaceholder')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                {...register('email', {
                                    required: t('login.emailRequired'),
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: t('login.emailInvalid'),
                                    },
                                })}
                            />

                            <Box>
                                <TextField
                                    label={t('login.passwordLabel')}
                                    type={showPw ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    placeholder={t('login.passwordPlaceholder')}
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPw((v) => !v)}
                                                        edge="end"
                                                        aria-label={showPw ? t('login.hidePassword') : t('login.showPassword')}
                                                    >
                                                        {showPw ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                    {...register('password', {
                                        required: t('login.passwordRequired'),
                                    })}
                                />

                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'primary.main' } }}
                                        {...register('rememberMe')}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        {t('login.rememberMe')}
                                    </Typography>
                                }
                                sx={{ mt: -1 }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t('login.submitting') : t('login.submit')}
                            </Button>
                        </Box>

                        <Divider sx={{ my: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('login.orContinueWith')}
                            </Typography>
                        </Divider>

                        <Button
                            component="a"
                            href={GOOGLE_OAUTH_URL}
                            // O Google tira-nos da SPA e o state do router perde-se — guarda a página a retomar.
                            onClick={() => rememberLoginDestination(destination)}
                            variant="outlined"
                            size="large"
                            fullWidth
                            startIcon={<GoogleIcon />}
                            sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}
                        >
                            {t('login.google')}
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary" >
                            {t('login.noAccount')}{' '}
                            <Link
                                to="/register"
                                style={{ color: '#3730A3', fontWeight: 500, textDecoration: 'none' }}
                            >
                                {t('login.createAccount')}
                            </Link>
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                            <Typography
                                component={Link}
                                to="/forgot-password"
                                variant="caption"
                                sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}
                            >
                                {t('login.forgotPassword')}
                            </Typography>
                        </Box>
                    </Paper>
                </AuthLayout>

                {/* Dialog de selecção de empresa — email associado a vários tenants */}
                <Dialog open={!!tenantOptions} maxWidth="xs" fullWidth onClose={() => { setTenantOptions(null); setPendingCredentials(null); setIsGoogleTenantChoice(false) }}>
                    <DialogTitle sx={{ fontWeight: 700 }}>{t('login.chooseCompanyTitle')}</DialogTitle>
                    <DialogContent sx={{ px: 0 }}>
                        <List>
                            {tenantOptions?.map((empresa) => (
                                <ListItemButton
                                    key={empresa.tenantId}
                                    onClick={() => handleTenantChosen(empresa.tenantId)}
                                    disabled={isChoosingTenant}
                                >
                                    <ListItemAvatar>
                                        <Avatar src={empresa.logoUrl ?? undefined} sx={{ bgcolor: 'brand.teal50', color: 'primary.main' }}>
                                            <ApartmentOutlined />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={empresa.nome} secondary={empresa.schemaName} />
                                </ListItemButton>
                            ))}
                        </List>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <SecondaryButton onClick={() => { setTenantOptions(null); setPendingCredentials(null); setIsGoogleTenantChoice(false) }} disabled={isChoosingTenant}>
                            {t('common:actions.cancel')}
                        </SecondaryButton>
                    </DialogActions>
                </Dialog>

                {/* Dialog "sem conta associada" — login Google de um email sem tenant */}
                <Dialog open={noAccountEmail !== null} maxWidth="xs" fullWidth onClose={closeNoAccount}>
                    {noAccountPhase === 'confirm' ? (
                        <>
                            <DialogTitle sx={{ fontWeight: 700 }}>{t('login.noAccountTitle')}</DialogTitle>
                            <DialogContent>
                                <Typography variant="body2" color="text.secondary">
                                    {t('login.noAccountQuestion', { email: noAccountEmail })}
                                </Typography>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <SecondaryButton onClick={closeNoAccount}>
                                    {t('login.noAccountDecline')}
                                </SecondaryButton>
                                <Button variant="contained" onClick={() => setNoAccountPhase('form')}>
                                    {t('login.noAccountCreate')}
                                </Button>
                            </DialogActions>
                        </>
                    ) : (
                        <Box component="form" onSubmit={handleSubmitCompany(onCreateCompany)} noValidate>
                            <DialogTitle sx={{ fontWeight: 700 }}>{t('login.noAccountFormTitle')}</DialogTitle>
                            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('login.noAccountFormSubtitle', { email: noAccountEmail })}
                                </Typography>
                                <TextField
                                    label={t('register.company.nameLabel')}
                                    placeholder={t('register.company.namePlaceholder')}
                                    autoComplete="organization"
                                    autoFocus
                                    error={!!companyErrors.nome}
                                    helperText={companyErrors.nome?.message}
                                    {...registerCompany('nome', {
                                        required: t('register.company.nameRequired'),
                                        minLength: { value: 2, message: t('register.company.nameTooShort') },
                                    })}
                                />
                                <TextField
                                    label={t('register.company.phoneLabel')}
                                    type="tel"
                                    placeholder={t('register.company.phonePlaceholder')}
                                    autoComplete="tel"
                                    error={!!companyErrors.telefone}
                                    helperText={companyErrors.telefone?.message}
                                    {...registerCompany('telefone', {
                                        required: t('register.company.phoneRequired'),
                                        minLength: { value: 7, message: t('register.company.phoneInvalid') },
                                    })}
                                />
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <SecondaryButton onClick={closeNoAccount} disabled={isCreatingCompany}>
                                    {t('common:actions.cancel')}
                                </SecondaryButton>
                                <Button type="submit" variant="contained" disabled={isCreatingCompany}>
                                    {isCreatingCompany ? t('login.noAccountSubmitting') : t('login.noAccountSubmit')}
                                </Button>
                            </DialogActions>
                        </Box>
                    )}
                </Dialog>

                {/* Dialog de boas-vindas — primeiro login: a mudança de palavra-passe acontece aqui mesmo */}
                <Dialog open={welcomeOpen} maxWidth="xs" fullWidth>
                    {welcomePhase === 'intro' && (
                        <>
                            <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 3, px: 4 }}>
                                <Box
                                    sx={{
                                        width: 64, height: 64, borderRadius: '50%',
                                        bgcolor: 'brand.teal50', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2.5,
                                    }}
                                >
                                    <WavingHandOutlined sx={{ fontSize: 32, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t('login.welcomeTitle', { name: welcomeName })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('login.welcomeDescription')}
                                </Typography>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
                                <Button variant="outlined" onClick={finishFirstLogin}>
                                    {t('login.later')}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => { setPwdApiError(''); setWelcomePhase('form') }}
                                >
                                    {t('login.updatePassword')}
                                </Button>
                            </DialogActions>
                        </>
                    )}

                    {welcomePhase === 'form' && (
                        <Box component="form" onSubmit={handleSubmitPwd(onChangeFirstPassword)} noValidate>
                            <DialogTitle sx={{ fontWeight: 700 }}>{t('login.firstPasswordTitle')}</DialogTitle>
                            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {firstLoginPassword
                                        ? t('login.firstPasswordSubtitle')
                                        : t('login.firstPasswordSubtitleWithCurrent')}
                                </Typography>

                                {pwdApiError && (
                                    <Alert severity="error" sx={{ borderRadius: 2 }}>{pwdApiError}</Alert>
                                )}

                                {!firstLoginPassword && (
                                    <TextField
                                        label={t('login.currentPasswordLabel')}
                                        type={showPw ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        error={!!pwdErrors.passwordActual}
                                        helperText={pwdErrors.passwordActual?.message}
                                        {...registerPwd('passwordActual', {
                                            required: t('login.currentPasswordRequired'),
                                        })}
                                    />
                                )}

                                <Box>
                                    <TextField
                                        label={t('login.newPasswordLabel')}
                                        type={showPw ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        autoFocus
                                        fullWidth
                                        error={!!pwdErrors.novaPassword}
                                        helperText={pwdErrors.novaPassword?.message}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowPw((v) => !v)}
                                                            edge="end"
                                                            aria-label={showPw ? t('login.hidePassword') : t('login.showPassword')}
                                                        >
                                                            {showPw ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        {...registerPwd('novaPassword', {
                                            required: t('login.newPasswordRequired'),
                                            minLength: { value: 8, message: t('login.newPasswordTooShort') },
                                        })}
                                    />
                                    <PasswordStrengthBar password={novaPasswordValue} />
                                </Box>

                                <TextField
                                    label={t('login.confirmPasswordLabel')}
                                    type={showPw ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    fullWidth
                                    error={!!pwdErrors.confirmarPassword}
                                    helperText={pwdErrors.confirmarPassword?.message}
                                    {...registerPwd('confirmarPassword', {
                                        required: t('login.confirmPasswordRequired'),
                                        validate: (v) =>
                                            v === novaPasswordValue || t('login.passwordsDontMatch'),
                                    })}
                                />
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                                <SecondaryButton
                                    onClick={() => setWelcomePhase('intro')}
                                    disabled={isChangingPassword}
                                >
                                    {t('login.back')}
                                </SecondaryButton>
                                <Button type="submit" variant="contained" disabled={isChangingPassword}>
                                    {isChangingPassword
                                        ? t('login.changePasswordSubmitting')
                                        : t('login.changePasswordSubmit')}
                                </Button>
                            </DialogActions>
                        </Box>
                    )}

                    {welcomePhase === 'done' && (
                        <>
                            <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 3, px: 4 }}>
                                <Box
                                    sx={{
                                        width: 64, height: 64, borderRadius: '50%',
                                        bgcolor: 'brand.teal50', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2.5,
                                    }}
                                >
                                    <CheckCircleOutlined sx={{ fontSize: 32, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t('login.firstPasswordDoneTitle')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('login.firstPasswordDoneDescription')}
                                </Typography>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                                <Button variant="contained" onClick={finishFirstLogin}>
                                    {t('login.continue')}
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </>
        )
    }

    return <Navigate to={destination ?? '/'} replace />
}
